import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DateTimeHelper } from "../../../../../common/helpers/datetime/datetime.helper";
import { ToastService } from "../../../../../main/services";
import { TenderAwardsService } from "../../../../services/tender-awards.service";

@Component({
    selector: "jft-edit-award-modal",
    templateUrl: "./edit-award-modal.component.html",
    styleUrls: ["./edit-award-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class EditAwardModalComponent implements OnInit, OnDestroy {
    @Input() tender: any;
    @Input() offer: any;
    @Input() supplierGroup: any;

    public form: FormGroup;
    public loading: boolean = false;
    // Keep in sync with AWARD_COMMENT_MAX_LENGTH in tender_awards_edit.py
    public readonly awardCommentMaxLength = 2000;
    private lastEditedField: "percent" | "volume" = "volume";
    private destroy$ = new Subject<void>();

    constructor(
        public activeModal: NgbActiveModal,
        private awardsService: TenderAwardsService,
        private toast: ToastService,
    ) {
        this.form = new FormGroup({
            percent_awarded: new FormControl("", [
                Validators.required,
                Validators.min(0),
                Validators.max(100),
            ]),
            volume: new FormControl("", [Validators.required]),
            award_self_supply: new FormControl(""),
            award_comment: new FormControl("", [
                Validators.maxLength(2000),
            ]),
            date_contract_start: new FormControl(""),
            date_contract_end: new FormControl(""),
        });
    }

    ngOnInit() {
        if (this.offer) {
            const percent = this.offer.awarded_percent || 0;
            const volume =
                this.offer.awarded_volume != null
                    ? this.offer.awarded_volume
                    : this.offer.volume
                      ? (this.offer.volume * percent) / 100
                      : 0;

            this.form.patchValue({
                percent_awarded: Math.round(percent),
                volume: this.formatVolume(volume),
                award_self_supply: this.offer.award_self_supply || "",
                award_comment: this.offer.award_comment || "",
                date_contract_start: this.offer.contract_start
                    ? this.parseDate(this.offer.contract_start)
                    : null,
                date_contract_end: this.offer.contract_end
                    ? this.parseDate(this.offer.contract_end)
                    : null,
            });

            // Bidirectional sync between percent and volume
            this.form.controls["percent_awarded"].valueChanges
                .pipe(takeUntil(this.destroy$))
                .subscribe((value) => {
                    this.lastEditedField = "percent";
                    if (this.offer?.volume) {
                        const vol =
                            (parseFloat(value) * this.offer.volume) / 100;
                        this.form.controls["volume"].patchValue(
                            this.formatVolume(vol),
                            {
                                emitEvent: false,
                                onlySelf: true,
                            },
                        );
                    }
                });

            this.form.controls["volume"].valueChanges
                .pipe(takeUntil(this.destroy$))
                .subscribe((value) => {
                    this.lastEditedField = "volume";
                    if (this.offer?.volume) {
                        const pct = (
                            (this.parseNumericValue(value) /
                                this.offer.volume) *
                            100
                        ).toFixed(0);
                        this.form.controls["percent_awarded"].patchValue(pct, {
                            emitEvent: false,
                            onlySelf: true,
                        });
                    }
                });
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private parseNumericValue(value: any): number {
        return parseFloat(value.toString().replace(/[^\d.-]/g, "")) || 0;
    }

    public formatVolume(value: number | string): string {
        const num =
            typeof value === "string"
                ? parseFloat(value.toString().replace(/[^\d.-]/g, ""))
                : value;
        if (isNaN(num)) return "";
        return Math.round(num).toLocaleString("en-US");
    }

    public onVolumeBlur() {
        const raw = this.form.controls["volume"].value;
        this.form.controls["volume"].patchValue(this.formatVolume(raw), {
            emitEvent: false,
        });
    }

    public getRemainingVolume(): number {
        if (!this.offer?.volume) return 0;
        const awardedVolume = this.parseNumericValue(
            this.form.controls["volume"].value,
        );
        return Math.max(0, this.offer.volume - awardedVolume);
    }

    private parseDate(dateString: string) {
        if (!dateString) return null;

        try {
            // Parse ISO date string to extract date components without timezone conversion
            // This prevents the one-day offset issue when converting UTC dates to local timezone
            const ymd = DateTimeHelper.parseISODateToYMD(dateString);
            if (ymd) {
                return ymd;
            }

            // Fallback to old method if parsing fails
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return null;

            // Convert to NgbDate format
            return {
                year: date.getFullYear(),
                month: date.getMonth() + 1, // NgBootstrap months are 1-based
                day: date.getDate(),
            };
        } catch (error) {
            console.error("Error parsing date:", error);
            return null;
        }
    }

    public save() {
        if (this.form.invalid) {
            this.toast.error("Please fill in all required fields correctly");
            return;
        }

        this.loading = true;

        // Build award data based on which field was last edited
        const awardData: any = {
            award_self_supply: this.form.value.award_self_supply,
            award_comment: (this.form.value.award_comment || "").trim(),
            date_contract_start: this.form.value.date_contract_start,
            date_contract_end: this.form.value.date_contract_end,
        };

        if (this.lastEditedField === "volume") {
            // Volume was last edited - send it as source of truth
            const volumeRaw = this.form.value.volume;
            awardData.awarded_volume = volumeRaw
                ? this.parseNumericValue(volumeRaw)
                : null;
        } else {
            // Percentage was last edited - send it as source of truth
            awardData.awarded_percent = this.form.value.percent_awarded;
        }

        // Call the API to update the award
        this.awardsService
            .editAward(this.tender.id, this.offer.id, awardData)
            .subscribe({
                next: (response) => {
                    this.loading = false;
                    this.toast.success("Award updated successfully");
                    this.activeModal.close({
                        updated: true,
                        offerId: this.offer.id,
                        response: response,
                    });
                },
                error: (error) => {
                    this.loading = false;
                    this.toast.error(
                        error.error?.details || "Failed to update award",
                    );
                    console.error("Edit award error:", error);
                },
            });
    }

    public cancel() {
        this.activeModal.dismiss("cancel");
    }

    public getSupplierName(): string {
        // Try to get supplier name from supplier group first
        if (this.supplierGroup?.supplier?.name) {
            return this.supplierGroup.supplier.name;
        }

        // Fallback to offer data
        if (this.offer?.supplier?.name) {
            return this.offer.supplier.name;
        }

        return "N/A";
    }

    public getContractStartDate(offer: any): Date | null {
        if (offer?.contract_start) {
            // Parse ISO date string to extract date components without timezone conversion
            const ymd = DateTimeHelper.parseISODateToYMD(offer.contract_start);
            if (ymd) {
                // Create a date in local timezone using the extracted components
                return new Date(ymd.year, ymd.month - 1, ymd.day);
            }
            return new Date(offer.contract_start);
        }
        return null;
    }

    public getContractEndDate(offer: any): Date | null {
        if (offer?.contract_end) {
            // Parse ISO date string to extract date components without timezone conversion
            const ymd = DateTimeHelper.parseISODateToYMD(offer.contract_end);
            if (ymd) {
                // Create a date in local timezone using the extracted components
                return new Date(ymd.year, ymd.month - 1, ymd.day);
            }
            return new Date(offer.contract_end);
        }
        return null;
    }
}
